'use client';

import { Box, Flex, freddyTheme, Typography } from '@coolblue-development/freddy';
import showProductPlusTrigger from '@coolblue-development/ga4-events/triggers/ProductPlus/showProductPlusTrigger';
import { Checkmark } from '@coolblue-development/icons';
import { TrackImmediate } from '@coolblue-development/next-analytics/client';
import { CAMPAIGN_TYPE } from '@features/product/components/OrderBlock/FreeWashingOrDryingProposition/constants';
import { useTranslation } from '@i18n/useTranslation';
import { useCallback, useEffect, useRef, useState } from 'react';

import ExpandableBox from '../ExpandableBox';
import SubscriptionForm from '../SubscriptionForm';

export default function NewEntrance({
  isFreeDrying,
  hasSubscription = false
}: {
  isFreeDrying: boolean;
  hasSubscription?: boolean;
}) {
  const { t, Trans } = useTranslation('webshop.productPage.freeWashing');
  const [showEmailForm, setShowEmailForm] = useState<boolean>(false);
  const [isSubscribed, setIsSubscribed] = useState<boolean>(hasSubscription);
  const successMessageRef = useRef<HTMLDivElement>(null);

  const dictionaryKeySuffix = isFreeDrying ? 'Drying' : '';

  const handleSubscriptionSuccess = useCallback(() => {
    setIsSubscribed(true);
    setShowEmailForm(false);
  }, []);

  useEffect(() => {
    if (!hasSubscription && isSubscribed && successMessageRef.current) {
      setTimeout(() => {
        successMessageRef.current?.focus();
      }, 100);
    }
  }, [hasSubscription, isSubscribed]);

  const handleToggleEmailForm = useCallback(() => {
    if (isSubscribed) return;

    setShowEmailForm(prev => !prev);
  }, [isSubscribed]);

  return (
    <Flex direction='column' gap={200}>
      <TrackImmediate trigger={showProductPlusTrigger({ sub_feature: 'free_washing' })} />

      <Box>
        <Typography fontWeight={600}>
          <Trans
            components={{
              strong: <Typography component='span' fontWeight={700} color='accented' />
            }}
            i18nKey={`blockTitle${dictionaryKeySuffix}`}
            t={t}
          />
        </Typography>
      </Box>

      <ExpandableBox
        isExpanded={showEmailForm}
        isSubscribed={isSubscribed}
        onClick={handleToggleEmailForm}
        expandedContent={
          <SubscriptionForm
            onSuccessAction={handleSubscriptionSuccess}
            type={isFreeDrying ? CAMPAIGN_TYPE.FreeDrying : CAMPAIGN_TYPE.FreeWashing}
          />
        }
      >
        <Box flex={1}>
          <Typography fontWeight={700} mb={100} color='interactiveBase'>
            {t('title')}
          </Typography>

          <Typography variant='body' color='subtle' mb={0}>
            {t(`newDesign.description${dictionaryKeySuffix}`)}
          </Typography>

          {isSubscribed && (
            <Flex
              ref={successMessageRef}
              alignItems='center'
              gap={100}
              mt={100}
              role='status'
              aria-atomic='true'
              tabIndex={-1}
              sx={{ outline: 'none' }}
            >
              <Checkmark color={freddyTheme.color.icon.sentimentPositiveContrast} />
              <Typography color='sentimentPositiveContrast'>
                {t('subscriptionSuccessMessage')}
              </Typography>
            </Flex>
          )}
        </Box>
      </ExpandableBox>
    </Flex>
  );
}
